import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import ShortsClip from './clip';
import { activeIndexFromEntries, hlsWindowIndices, ACTIVE_THRESHOLD } from '../../lib/shorts';

const PAGE_LIMIT = 10;

// A post can carry several portrait clips, so uuidv4 alone names the post, not
// the clip — clip identity (keys, dedup, subtitle cache) needs the file index
// too. Engagement and seen-reporting stay keyed by uuidv4 on purpose: likes,
// comments and the seen tier are per-post.
const clipKey = clip => `${clip.uuidv4}:${clip.fileIndex || 0}`;

@observer
class ShortsFeed extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clips: [],
            activeIndex: 0,
            muted: true,
            offset: 0,
            hasMore: true,
            loading: true,
            subtitles: {},
            // Per-clip { likes: [], comments: [] } - full arrays, fetched lazily for
            // whichever clip is active. get-shorts.js only returns likeCount/commentCount
            // (cheap aggregate counts for a page of candidates); the actual Likes/Comments
            // components need the full arrays (to know if the current user already
            // liked/commented, and to render the comment list itself), so those are
            // fetched on demand rather than bloating every page of the shorts feed.
            engagement: {},
        };
        this.reportedSeen = new Set();
        this.observer = null;
        this.containerRef = null;
        this.loadingPage = false;
        this.engagementLoading = new Set();
    }

    componentDidMount() {
        this.loadPage();
    }

    componentDidUpdate(prevProps, prevState) {
        // New pages append children to the same container element, so the ref
        // callback (which only fires once, when the DOM node itself is created)
        // never sees them. Re-observing whenever the clip count changes is what
        // actually gets newly appended clips under IntersectionObserver.
        if (prevState.clips.length !== this.state.clips.length) {
            this.observeChildren();
        }
    }

    componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    loadPage = async () => {
        const { articleStore } = this.props.stores;
        const { shortUuidv4 } = this.props;
        const { offset, hasMore } = this.state;
        if (this.loadingPage || !hasMore) {
            return;
        }
        this.loadingPage = true;
        this.setState({ loading: true });

        const response = await util.fetchApi('/api/articles/shorts', { method: 'GET', publish: false }, {
            hostname: articleStore.currentHostname,
            offset,
            limit: PAGE_LIMIT,
            uuidv4: offset === 0 && shortUuidv4 ? shortUuidv4 : undefined,
        });

        if (response?.status !== 200 || !Array.isArray(response.data)) {
            this.loadingPage = false;
            this.setState({ loading: false, hasMore: false });
            return;
        }

        this.setState((prevState) => {
            // The seen set shifts while the viewer swipes, so a clip can move between
            // tiers between requests. De-duplicating by clip key keeps the accumulated
            // list stable and lets the endpoint stay stateless.
            const known = new Set(prevState.clips.map(clipKey));
            const fresh = response.data.filter(c => !known.has(clipKey(c)));
            return {
                clips: [...prevState.clips, ...fresh],
                offset: prevState.offset + response.data.length,
                // A full page that added nothing new means every clip the backend would
                // return right now is already known. If the viewer is already sitting on
                // the last clip, activeIndex never changes again, so nothing would ever
                // re-trigger paging - stop claiming there is more instead of stalling.
                hasMore: fresh.length > 0 && response.data.length >= PAGE_LIMIT,
                loading: false,
            };
        }, () => {
            this.loadingPage = false;
            this.loadSubtitlesAround(this.state.activeIndex);
            this.loadEngagement(this.state.clips[this.state.activeIndex]);
        });
    }

    loadSubtitlesAround = async (index) => {
        const { appState, articleStore } = this.props.stores;
        const { clips, subtitles } = this.state;
        const wanted = [index, index + 1]
            .map(i => clips[i])
            .filter(clip => clip && clip.hasSubtitles && !subtitles[clipKey(clip)]);

        for (const clip of wanted) {
            // eslint-disable-next-line no-await-in-loop
            const response = await util.fetchApi(
                `/api/articles/shorts/${clip.uuidv4}/subtitles`,
                { method: 'GET', publish: false },
                {
                    hostname: articleStore.currentHostname,
                    lang: appState.language || '',
                    fileIndex: clip.fileIndex || 0,
                },
            );
            if (response?.status === 200 && response.data) {
                this.setState(prevState => ({
                    subtitles: { ...prevState.subtitles, [clipKey(clip)]: response.data.segments || [] },
                }));
            }
        }
    }

    // Fetches the full like/comment arrays for one clip's article - the heart and
    // comment-icon on the rail (clip.js) need these to know whether the current
    // user already liked/commented and to render the actual comment list, which
    // clip.likeCount/commentCount (aggregate numbers from get-shorts.js) can't
    // provide. Both /api/likes and /api/comments require a logged-in user
    // (restrictAPI), so anonymous viewers keep seeing only the static counts -
    // that mirrors how Likes/Comments behave everywhere else in the app (they
    // fall back to a "login to like/comment" prompt rather than fetching).
    // `force` re-fetches after a like/unlike so the rail count stays accurate.
    loadEngagement = async (clip, { force = false } = {}) => {
        if (!clip) {
            return null;
        }
        const { userStore } = this.props.stores;
        const { user } = userStore;
        if (!user || !user.id) {
            return null;
        }
        const { engagement } = this.state;
        if (!force && engagement[clip.uuidv4]) {
            return engagement[clip.uuidv4];
        }
        if (this.engagementLoading.has(clip.uuidv4)) {
            return null;
        }
        this.engagementLoading.add(clip.uuidv4);
        const [likesResponse, commentsResponse] = await Promise.all([
            util.fetchApi('/api/likes', { method: 'GET', publish: false }, { type: 'article', objectId: clip.id }),
            util.fetchApi('/api/comments', { method: 'GET', publish: false }, { type: 'article', objectId: clip.id }),
        ]);
        this.engagementLoading.delete(clip.uuidv4);
        const data = {
            likes: likesResponse?.status === 200 && Array.isArray(likesResponse.data) ? likesResponse.data : [],
            comments: commentsResponse?.status === 200 && Array.isArray(commentsResponse.data) ? commentsResponse.data : [],
        };
        this.setState(prevState => ({
            engagement: { ...prevState.engagement, [clip.uuidv4]: data },
        }));
        return data;
    }

    // (Re)attaches the observer to every current child of the scroll container.
    // Called both from the ref callback (first mount) and from
    // componentDidUpdate (whenever paging appends new clips) - disconnecting
    // first keeps a child from ever being observed twice.
    observeChildren = () => {
        const el = this.containerRef;
        if (!el || !this.observer) {
            return;
        }
        this.observer.disconnect();
        Array.from(el.children).forEach(child => this.observer.observe(child));
    }

    observeContainer = (el) => {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.containerRef = el;
        if (!el) {
            this.observer = null;
            return;
        }
        this.observer = new IntersectionObserver((entries) => {
            const next = activeIndexFromEntries(entries, this.state.activeIndex);
            if (next !== this.state.activeIndex) {
                this.setState({ activeIndex: next }, () => {
                    this.loadSubtitlesAround(next);
                    this.loadEngagement(this.state.clips[next]);
                    if (next >= this.state.clips.length - 3) {
                        this.loadPage();
                    }
                });
            }
        }, { root: el, threshold: [0, ACTIVE_THRESHOLD, 1] });

        this.observeChildren();
    }

    onSeen = async (clip) => {
        if (this.reportedSeen.has(clip.uuidv4)) {
            return;
        }
        this.reportedSeen.add(clip.uuidv4);
        // Fire-and-forget: if this fails, ordering degrades to newest-first.
        await util.fetchApi('/api/videoseens', { method: 'POST', publish: false }, {
            article: clip.id,
        });
    }

    onToggleMute = () => {
        this.setState(prevState => ({ muted: !prevState.muted }));
    }

    render() {
        const { stores } = this.props;
        const { clips, activeIndex, muted, subtitles, loading, engagement } = this.state;

        if (clips.length === 0) {
            return (
                <div
                    class='d-flex flex-column align-items-center justify-content-center text-body-secondary w-100'
                    // flex: 1 1 auto (not a fixed vh) - this component is rendered inside
                    // the sticky, height-locked wrapper the frontpage feed builds for the
                    // shorts tab (see routes/frontpage/loggedIn.js and notLoggedIn.js),
                    // which already reserves exactly the visible area beneath the fixed
                    // header + tab bar. Filling that flex slot (rather than guessing a vh
                    // value) keeps the empty/loading state the same size as a real clip.
                    style='flex: 1 1 auto; min-height: 0;'
                >
                    {loading ? (
                        <i class='fa-solid fa-spinner fa-spin fa-2x' />
                    ) : (
                        <>
                            <i class='fa-duotone fa-video-slash fa-2x mb-3' />
                            <Text id='shorts.empty'>No portrait videos yet</Text>
                        </>
                    )}
                </div>
            );
        }

        const keepAlive = hlsWindowIndices(activeIndex, clips.length);

        return (
            <div
                ref={this.observeContainer}
                class='w-100'
                // flex: 1 1 auto (not height: 100dvh) - the frontpage feed's shorts tab
                // wraps the feed-tab-bar + this component in a sticky, height-locked
                // container sized to `calc(100dvh - chromeHeight)` (chromeHeight sourced
                // from the same currentCustomer.logoHeight + topSheetOffset app.js uses
                // for #app's own padding-top - see routes/frontpage/loggedIn.js and
                // notLoggedIn.js). This component just fills whatever's left after the
                // tab bar via flexbox, so it never needs to know the tab bar's own
                // height. 100dvh here would double-count the fixed header (already
                // reserved via #app's padding-top) and the tab bar, pushing the bottom
                // of every clip - including the subtitle overlay - below the fold.
                style='flex: 1 1 auto; min-height: 0; overflow-y: auto; scroll-snap-type: y mandatory; -webkit-overflow-scrolling: touch;'
            >
                {clips.map((clip, index) => (
                    <ShortsClip
                        key={`short-${clipKey(clip)}`}
                        stores={stores}
                        clip={clip}
                        index={index}
                        isActive={index === activeIndex}
                        keepAlive={keepAlive.includes(index)}
                        muted={muted}
                        subtitles={subtitles[clipKey(clip)] || []}
                        engagement={engagement[clip.uuidv4]}
                        onLoadEngagement={this.loadEngagement}
                        onToggleMute={this.onToggleMute}
                        onSeen={this.onSeen}
                    />
                ))}
            </div>
        );
    }
}

export default ShortsFeed;
